/**
 * OpenNebula Elastic IPs + site-to-site VPN. Endpoints (v2 s10003):
 *   /elastic_ips (+ /:id/attach, /:id/detach, DELETE /:id)
 *   /vpn/gateways, /vpn/ike_policies, /vpn/ipsec_policies, /vpn/s2s_connections
 */
import { useMutation, useQuery, useQueryClient, type UseMutationOptions } from '@tanstack/react-query';
import { serviceApi, onPath, onKeys, unwrap } from './_shared';

const arr = <T,>(body: unknown, key: string): T[] =>
  Array.isArray(body) ? (body as T[]) : ((body as Record<string, unknown>)?.[key] as T[]) ?? [];

// ---- Elastic IPs ---------------------------------------------------------

export interface ElasticIp {
  id: string;
  ip?: string;
  /** The API field is `ip_address`; `ip` kept as a tolerant fallback. */
  ip_address?: string;
  instance_id?: string;
  instance_name?: string;
  state?: string;
  /** 'alias' (attached as NIC alias) vs 'floating'. */
  use_type?: string;
}

export function useElasticIps(serviceId: string | undefined) {
  return useQuery({
    queryKey: onKeys.elasticIps(serviceId ?? ''),
    enabled: !!serviceId,
    retry: false,
    queryFn: async () => arr<ElasticIp>(unwrap(await serviceApi.get(onPath(serviceId!, 'elastic_ips'))), 'elastic_ips'),
  });
}

export function useAllocateElasticIp(serviceId: string, options?: UseMutationOptions<unknown, unknown, void>) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async () => (await serviceApi.post(onPath(serviceId, 'elastic_ips'), {})).data,
    ...options,
    onSuccess: (...a) => { qc.invalidateQueries({ queryKey: onKeys.elasticIps(serviceId) }); options?.onSuccess?.(...a); },
  });
}

/** Attach an elastic IP to an instance as a NIC alias or a floating (1:1 NAT) IP. */
export function useAttachElasticIp(
  serviceId: string,
  options?: UseMutationOptions<unknown, unknown, { id: string; instance_id: string; use_type?: 'alias' | 'floating' }>,
) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async ({ id, instance_id, use_type }: { id: string; instance_id: string; use_type?: 'alias' | 'floating' }) =>
      (await serviceApi.post(onPath(serviceId, `elastic_ips/${id}/attach`), { instance_id, ...(use_type ? { use_type } : {}) })).data,
    ...options,
    onSuccess: (...a) => { qc.invalidateQueries({ queryKey: onKeys.elasticIps(serviceId) }); options?.onSuccess?.(...a); },
  });
}

export function useDetachElasticIp(serviceId: string, options?: UseMutationOptions<unknown, unknown, string>) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (id: string) => (await serviceApi.post(onPath(serviceId, `elastic_ips/${id}/detach`), {})).data,
    ...options,
    onSuccess: (...a) => { qc.invalidateQueries({ queryKey: onKeys.elasticIps(serviceId) }); options?.onSuccess?.(...a); },
  });
}

export function useReleaseElasticIp(serviceId: string, options?: UseMutationOptions<unknown, unknown, string>) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (id: string) => (await serviceApi.delete(onPath(serviceId, `elastic_ips/${id}`))).data,
    ...options,
    onSuccess: (...a) => { qc.invalidateQueries({ queryKey: onKeys.elasticIps(serviceId) }); options?.onSuccess?.(...a); },
  });
}

// ---- VPN -----------------------------------------------------------------

export interface VpnGateway {
  id: string;
  name?: string;
  /** Provisioning state: `"ready"` once usable. */
  status?: string;
  public_ip?: string;
}
export interface VpnPolicy {
  id: string;
  name?: string;
  description?: string;
  /** IKE only. */
  ike_version?: string;
  encryption_algorithm?: string;
  hash_algorithm?: string;
  /** IKE only. */
  dh_group?: string;
  authentication_method?: string;
  /** IPsec only (esp/ah). */
  protocol?: string;
  /** IPsec only (enable/disable). */
  pfs?: string;
  mode?: string;
  lifetime?: number;
  status?: string;
  dpd_action?: string;
  dpd_interval?: number;
  dpd_timeout?: number;
}
export interface VpnConnection {
  id: string;
  name?: string;
  description?: string;
  vpn_gateway_id?: string;
  ike_policy_id?: string;
  ipsec_policy_id?: string;
  remote_gateway_ip?: string;
  remote_local_subnet?: string;
  local_subnet_cidrs?: string;
  remote_subnet_cidrs?: string;
  status?: string;
  close_action?: string;
}

function useVpnList<T>(serviceId: string | undefined, path: string, key: readonly unknown[], envKey: string) {
  return useQuery({
    queryKey: key,
    enabled: !!serviceId,
    retry: false,
    queryFn: async () => arr<T>(unwrap(await serviceApi.get(onPath(serviceId!, path))), envKey),
  });
}

// NOTE: legacy paths are flat with underscores (vpn_gateways, NOT vpn/gateways);
// the response envelope key matches the path (e.g. `{ vpn_gateways: [...] }`).
export const useVpnGateways = (s: string | undefined) =>
  useVpnList<VpnGateway>(s, 'vpn_gateways', onKeys.vpnGateways(s ?? ''), 'vpn_gateways');
export const useIkePolicies = (s: string | undefined) =>
  useVpnList<VpnPolicy>(s, 'vpn_ike_policies', onKeys.vpnIke(s ?? ''), 'vpn_ike_policies');
export const useIpsecPolicies = (s: string | undefined) =>
  useVpnList<VpnPolicy>(s, 'vpn_ipsec_policies', onKeys.vpnIpsec(s ?? ''), 'vpn_ipsec_policies');
export const useS2sConnections = (s: string | undefined) =>
  useVpnList<VpnConnection>(s, 'vpn_s2s_connections', onKeys.vpnS2s(s ?? ''), 'vpn_s2s_connections');

function useVpnMutation(serviceId: string, path: string, invalidate: readonly unknown[], method: 'post' | 'delete') {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (arg: Record<string, unknown> | string) => {
      if (method === 'delete') return (await serviceApi.delete(onPath(serviceId, `${path}/${arg as string}`))).data;
      return (await serviceApi.post(onPath(serviceId, path), arg as Record<string, unknown>)).data;
    },
    onSuccess: () => qc.invalidateQueries({ queryKey: invalidate }),
  });
}

/** PUT `${path}/:id` — edit a VPN resource in place. Mirrors @console
 *  `mutate*Update`. The mutation arg carries the resource id + the new payload. */
function useVpnUpdate(serviceId: string, path: string, invalidate: readonly unknown[]) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async ({ id, payload }: { id: string; payload: Record<string, unknown> }) =>
      (await serviceApi.put(onPath(serviceId, `${path}/${id}`), payload)).data,
    onSuccess: () => qc.invalidateQueries({ queryKey: invalidate }),
  });
}

export const useCreateVpnGateway = (s: string) => useVpnMutation(s, 'vpn_gateways', onKeys.vpnGateways(s), 'post');
export const useDeleteVpnGateway = (s: string) => useVpnMutation(s, 'vpn_gateways', onKeys.vpnGateways(s), 'delete');
export const useUpdateVpnGateway = (s: string) => useVpnUpdate(s, 'vpn_gateways', onKeys.vpnGateways(s));
export const useCreateIkePolicy = (s: string) => useVpnMutation(s, 'vpn_ike_policies', onKeys.vpnIke(s), 'post');
export const useDeleteIkePolicy = (s: string) => useVpnMutation(s, 'vpn_ike_policies', onKeys.vpnIke(s), 'delete');
export const useUpdateIkePolicy = (s: string) => useVpnUpdate(s, 'vpn_ike_policies', onKeys.vpnIke(s));
export const useCreateIpsecPolicy = (s: string) => useVpnMutation(s, 'vpn_ipsec_policies', onKeys.vpnIpsec(s), 'post');
export const useDeleteIpsecPolicy = (s: string) => useVpnMutation(s, 'vpn_ipsec_policies', onKeys.vpnIpsec(s), 'delete');
export const useUpdateIpsecPolicy = (s: string) => useVpnUpdate(s, 'vpn_ipsec_policies', onKeys.vpnIpsec(s));
export const useCreateS2sConnection = (s: string) => useVpnMutation(s, 'vpn_s2s_connections', onKeys.vpnS2s(s), 'post');
export const useDeleteS2sConnection = (s: string) => useVpnMutation(s, 'vpn_s2s_connections', onKeys.vpnS2s(s), 'delete');
export const useUpdateS2sConnection = (s: string) => useVpnUpdate(s, 'vpn_s2s_connections', onKeys.vpnS2s(s));

// ---- VPN gateway readiness + Source NAT ---------------------------------

/** Poll a gateway's async provisioning readiness. */
export function useVpnGatewayReady(serviceId: string | undefined, gatewayId: string | undefined, enabled = true) {
  return useQuery({
    queryKey: [...onKeys.vpnGateways(serviceId ?? ''), gatewayId, 'check_ready'] as const,
    enabled: enabled && !!serviceId && !!gatewayId,
    retry: false,
    refetchInterval: 10_000,
    queryFn: async () => unwrap<{ ready?: boolean; state?: string }>(await serviceApi.get(onPath(serviceId!, `vpn_gateways/${gatewayId}/check_ready`))),
  });
}

export interface VpnSourceNat {
  id: string;
  name?: string;
  source_addr?: string;
  translation_addr?: string;
  dest_addr?: string;
}

// The list endpoint requires a gateway id (`?vpn_gateway_id=`); a gateway-less
// call returns HTTP 400. Stays disabled until a gateway is selected.
export function useVpnSourceNats(serviceId: string | undefined, gatewayId?: string) {
  return useQuery({
    queryKey: [...onKeys.vpnGateways(serviceId ?? ''), 'source_nats', gatewayId ?? ''] as const,
    enabled: !!serviceId && !!gatewayId,
    retry: false,
    queryFn: async () =>
      arr<VpnSourceNat>(
        unwrap(await serviceApi.get(onPath(serviceId!, 'vpn_source_nats'), { params: { vpn_gateway_id: gatewayId } })),
        'vpn_source_nats',
      ),
  });
}

export function useCreateSourceNat(serviceId: string, options?: UseMutationOptions<unknown, unknown, Record<string, unknown>>) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (p: Record<string, unknown>) => (await serviceApi.post(onPath(serviceId, 'vpn_source_nats'), p)).data,
    ...options,
    onSuccess: (...a) => { qc.invalidateQueries({ queryKey: [...onKeys.vpnGateways(serviceId), 'source_nats'] }); options?.onSuccess?.(...a); },
  });
}

export function useDeleteSourceNat(serviceId: string, options?: UseMutationOptions<unknown, unknown, string>) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (id: string) => (await serviceApi.delete(onPath(serviceId, `vpn_source_nats/${id}`))).data,
    ...options,
    onSuccess: (...a) => { qc.invalidateQueries({ queryKey: [...onKeys.vpnGateways(serviceId), 'source_nats'] }); options?.onSuccess?.(...a); },
  });
}

// ---- VPN gateway subnets (gateway-scoped network binding) -----------------
// Matches @console: GET /vpn_gateways/:id/subnets, POST …/attach_subnet,
// POST …/detach_subnet. The GET defaults to DISABLED — @console gates it the
// same way (the backend `GET subnets` endpoint may not be live yet); pass
// `enabled=true` once confirmed available.

export interface VpnGatewaySubnet {
  network_id?: string;
  nic_id?: number | string;
  name?: string;
  cidr?: string;
}

const gatewaySubnetsKey = (s: string, gatewayId: string) =>
  [...onKeys.vpnGateways(s), gatewayId, 'subnets'] as const;

export function useVpnGatewaySubnets(serviceId: string | undefined, gatewayId: string | undefined, enabled = false) {
  return useQuery({
    queryKey: gatewaySubnetsKey(serviceId ?? '', gatewayId ?? ''),
    enabled: enabled && !!serviceId && !!gatewayId,
    retry: false,
    staleTime: 30_000,
    queryFn: async () =>
      arr<VpnGatewaySubnet>(unwrap(await serviceApi.get(onPath(serviceId!, `vpn_gateways/${gatewayId}/subnets`))), 'subnets'),
  });
}

export function useAttachGatewaySubnet(
  serviceId: string,
  gatewayId: string,
  options?: UseMutationOptions<unknown, unknown, { network_id: string }>,
) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async ({ network_id }: { network_id: string }) =>
      (await serviceApi.post(onPath(serviceId, `vpn_gateways/${gatewayId}/attach_subnet`), { network_id })).data,
    ...options,
    onSuccess: (...a) => { qc.invalidateQueries({ queryKey: gatewaySubnetsKey(serviceId, gatewayId) }); options?.onSuccess?.(...a); },
  });
}

export function useDetachGatewaySubnet(
  serviceId: string,
  gatewayId: string,
  options?: UseMutationOptions<unknown, unknown, { network_id: string; nic_id: number }>,
) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async ({ network_id, nic_id }: { network_id: string; nic_id: number }) =>
      (await serviceApi.post(onPath(serviceId, `vpn_gateways/${gatewayId}/detach_subnet`), { network_id, nic_id })).data,
    ...options,
    onSuccess: (...a) => { qc.invalidateQueries({ queryKey: gatewaySubnetsKey(serviceId, gatewayId) }); options?.onSuccess?.(...a); },
  });
}
